import { useEffect, useRef, useState } from 'react';
import { getOpacity } from './getters';
import type { FeedEntry } from '~/services/layout/index.ts';
import type { Range } from '~/services/layout/types.ts';

const OFF_SCREEN_OPACITY_THRESHOLD: number = 0.4;
const OFF_SCREEN_OPACITY_LOADING_TIMEOUT: number = 500;

export function useOffScreenOpacity({
  ref,
  cellChildContainerRefs,
  feedEntries,
  offScreenOpacity,
  offScreenOpacitySwitch,
  isNextFeedLoading,
  isLoading,
  useOffScreenOpacityLoading = false,
  componentMaxWidth,
  isScrollingByClick,
}: {
  ref: any;
  cellChildContainerRefs: any;
  feedEntries: FeedEntry[];
  offScreenOpacity: number;
  offScreenOpacitySwitch: boolean;
  isNextFeedLoading: boolean;
  isLoading: boolean | undefined;
  useOffScreenOpacityLoading?: boolean;
  componentMaxWidth: number;
  isScrollingByClick: boolean;
}): boolean {
  const [isOffScreenOpacityLoading, setIsOffScreenOpacityLoading] =
    useState<boolean>(useOffScreenOpacityLoading);

  const timeoutRef = useRef<null | NodeJS.Timeout>(null);

  const rootMargin: string = getRootMargin({
    ref,
    componentMaxWidth,
  });

  useEffect(() => {
    if (!offScreenOpacitySwitch || isLoading) return;

    const getObserver: Function = (): IntersectionObserver => {
      return new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const opacity: number = entry.isIntersecting
              ? 1
              : getOpacity(offScreenOpacity as Range<101>);

            if (isScrollingByClick) return;

            // @ts-ignore
            entry.target.style.opacity = opacity;
          });
        },
        {
          root: ref.current,
          rootMargin,
          threshold: OFF_SCREEN_OPACITY_THRESHOLD,
        }
      );
    };

    const observer: IntersectionObserver = getObserver();

    const observeAllCells = () => {
      cellChildContainerRefs.current.forEach((cell: Element) => {
        if (cell instanceof Element) {
          return observer.observe(cell);
        }
      });
    };

    observeAllCells();

    const mutationObserver: MutationObserver = new MutationObserver(() => {
      clearTimeout(timeoutRef.current as NodeJS.Timeout);

      requestAnimationFrame(() => {
        observeAllCells();
      });

      if (isOffScreenOpacityLoading) {
        timeoutRef.current = setTimeout(() => {
          setIsOffScreenOpacityLoading(false);
        }, OFF_SCREEN_OPACITY_LOADING_TIMEOUT);
      }
    });

    mutationObserver.observe(ref.current, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });

    return () => {
      if (isNextFeedLoading) return;
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [
    offScreenOpacitySwitch,
    isNextFeedLoading,
    isLoading,
    feedEntries?.length,
    cellChildContainerRefs.current.length,
    componentMaxWidth,
    rootMargin,
    ref,
    isScrollingByClick,
  ]);

  useEffect(() => {
    if (offScreenOpacitySwitch && !isScrollingByClick) return;
    cellChildContainerRefs.current.forEach((cell: Element) => {
      if (cell instanceof Element) {
        return cell.setAttribute('style', 'opacity: 1');
      }
    });
  }, [offScreenOpacitySwitch, isScrollingByClick]);

  return isOffScreenOpacityLoading;
}

function getRootMargin({
  ref,
  componentMaxWidth,
}: {
  ref: any;
  componentMaxWidth: number;
}): string {
  if (!ref.current || !componentMaxWidth) return '0px';

  return `0px ${
    componentMaxWidth
      ? `-${
          componentMaxWidth
            ? Math.abs((ref.current.clientWidth - componentMaxWidth) / 2)
            : 0
        }px`
      : '0px'
  } 0px ${
    componentMaxWidth
      ? `-${
          componentMaxWidth
            ? Math.abs((ref.current.clientWidth - componentMaxWidth) / 2)
            : 0
        }px`
      : '0px'
  }`;
}
